import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const STORAGE_KEY = "artlenss-cookie-consent";

const COPY = {
  ar: {
    title: "الخصوصية والكوكيز",
    body:
      "نستخدم ملفات تعريف الارتباط (Cookies) لتحسين تجربتك، وتحليل أداء الموقع عبر Google Analytics، ولعرض إعلانات مدروسة من Google AdSense. تفاصيل كاملة في",
    privacyLink: "سياسة الخصوصية",
    accept: "أوافق",
    reject: "رفض غير الضروري",
    dismiss: "إغلاق",
  },
  en: {
    title: "Privacy & Cookies",
    body:
      "We use cookies to improve your experience, analyze site performance via Google Analytics, and serve relevant ads through Google AdSense. Full details in our",
    privacyLink: "Privacy Policy",
    accept: "I agree",
    reject: "Reject non-essential",
    dismiss: "Dismiss",
  },
};

const CookieConsent = () => {
  const { lang } = useLang();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      // slight delay so it doesn't fight the page hero animation
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const persist = (choice) => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() })
      );
    } catch { /* ignore private-mode failures */ }
    // Signal analytics/AdSense (optional — respected if the site adds consent-mode listeners)
    if (window.gtag) {
      window.gtag("consent", "update", {
        ad_storage: choice === "accepted" ? "granted" : "denied",
        analytics_storage: choice === "accepted" ? "granted" : "denied",
        ad_user_data: choice === "accepted" ? "granted" : "denied",
        ad_personalization: choice === "accepted" ? "granted" : "denied",
      });
    }
    setVisible(false);
  };

  if (!visible) return null;

  const c = COPY[lang] || COPY.en;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={c.title}
      data-testid="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-[80] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
    >
      <div className="mx-auto max-w-4xl pointer-events-auto">
        <div className="rounded-2xl border border-[#E6A95B]/30 bg-[#0d0d10]/95 backdrop-blur-xl shadow-2xl shadow-black/60 overflow-hidden">
          <div className="p-5 sm:p-6 flex items-start gap-4">
            <div className="hidden sm:grid place-items-center w-10 h-10 rounded-full bg-[#E6A95B]/15 text-[#E6A95B] shrink-0">
              <Cookie className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3 mb-2">
                <h3 className="font-display text-lg text-white">{c.title}</h3>
                <button
                  onClick={() => persist("dismissed")}
                  aria-label={c.dismiss}
                  className="text-white/40 hover:text-white transition p-1 -m-1"
                  data-testid="cookie-dismiss-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                {c.body}{" "}
                <Link to="/privacy" className="text-[#E6A95B] underline decoration-[#E6A95B]/40 hover:decoration-[#E6A95B]">
                  {c.privacyLink}
                </Link>
                .
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => persist("accepted")}
                  data-testid="cookie-accept-btn"
                  className="px-5 py-2 rounded-full bg-[#E6A95B] text-[#0A0A0C] font-bold text-sm hover:brightness-110 transition"
                >
                  {c.accept}
                </button>
                <button
                  onClick={() => persist("rejected")}
                  data-testid="cookie-reject-btn"
                  className="px-5 py-2 rounded-full border border-white/20 text-white/80 text-sm hover:border-[#E6A95B] hover:text-[#E6A95B] transition"
                >
                  {c.reject}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
